import { SENDER_TYPE } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { FileUpload } from '@backrs_ui/grow-ui/src/components/Lubav/FileUpload';
import { getUploadDocumentMessage } from '@backrs_ui/grow-ui/src/components/Lubav/fileUpload.util';
import {
  SolutionSessionMessage,
  DOCUMENT_NAMES,
  DOCUMENT_TYPES,
  MESSAGE_MODE,
} from '@backrs_ui/grow-ui/src/models/models';
import {
  PromptButtons,
  PromptOption,
} from '@backrs_ui/grow-ui/src/pages/OfficeHours/molecules/PromptButtons';
import {
  addSolutionSessionMessage,
  updateLastSolutionSessionMessage,
} from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  setUserInput,
  setUserMessage,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';
import { selectUserDocuments } from '@backrs_ui/grow-ui/src/redux/user/user';
import { DocumentInfo } from '@backrs_ui/grow-ui/src/redux/user/user.lubav.api';

const FILE_UPLOAD_STRING = appStrings.modules.chatBox.fileUpload;

const shouldUpdateFileUploadMessage = (
  message: SolutionSessionMessage,
  document: DocumentInfo | undefined,
  documentsLoading: boolean,
): boolean => {
  return !documentsLoading && !!document && !message.hasUpdatedForDocument;
};

type FileUploadQuestionProps = Readonly<{
  message: SolutionSessionMessage;
  sendQAMessage: (message: string, LLMInput?: string) => void;
  userId: number;
}>;

export const FileUploadQuestion = ({ message, sendQAMessage, userId }: FileUploadQuestionProps) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);
  const userDocuments = useAppSelector(selectUserDocuments);
  const documentsLoading = useAppSelector((state) => state.user.documentsLoading);

  if (!message.questionField) return null;

  // get document type from question field
  const documentType = message.questionField as keyof typeof DOCUMENT_NAMES;
  const documents = userDocuments?.[documentType];
  const document = documents?.[0]; // Get the first document

  const shouldUpdateMessage = shouldUpdateFileUploadMessage(message, document, documentsLoading);

  if (shouldUpdateMessage) {
    dispatch(
      createSessionAction(
        updateLastSolutionSessionMessage,
        activeSession,
      )({
        question: getUploadDocumentMessage({
          documentType,
          existingDocuments: documents
            ?.filter(
              (doc): doc is { filename: string; url: string } =>
                doc.filename !== null && doc.url !== null,
            )
            .map((doc) => ({ filename: doc.filename, url: doc.url })),
        }),
        mode: MESSAGE_MODE.QA,
        hasUpdatedForDocument: true,
      }),
    );
  }

  const hasExistingDocument =
    documents && documents.length > 0 && documents[0]?.filename && documents[0]?.url;

  if (hasExistingDocument && !message.userChoseNewUpload) {
    const uploadNewOption = FILE_UPLOAD_STRING.uploadANewDocument(DOCUMENT_NAMES[documentType]);
    const useExistingOption = FILE_UPLOAD_STRING.iWantToUseMyCurrentDocument(
      DOCUMENT_NAMES[documentType],
    );

    const promptOptions: PromptOption[] = [
      { id: 'upload_new', title: uploadNewOption },
      { id: 'use_existing', title: useExistingOption },
    ];

    return (
      <PromptButtons
        onClick={(option) => {
          if (option.id === 'upload_new') {
            dispatch(
              createSessionAction(
                addSolutionSessionMessage,
                activeSession,
              )({
                ...message,
                sender: SENDER_TYPE.AI,
                text: FILE_UPLOAD_STRING.great,
                question: getUploadDocumentMessage({
                  documentType,
                  existingDocuments: undefined,
                }),
                mode: MESSAGE_MODE.QA,
                userChoseNewUpload: true,
              }),
            );
          } else {
            sendQAMessage(useExistingOption, useExistingOption);
          }
        }}
        isTyping={false}
        promptOptions={promptOptions}
        variant="SINGLE_SELECT"
      />
    );
  }

  return (
    <FileUpload
      userId={userId}
      addToProfile={true}
      documentType={Object.values(DOCUMENT_TYPES).includes(documentType) ? documentType : undefined}
      onSubmit={(fileNames, content) => {
        if (!content) throw new Error('No content found');

        dispatch(
          createSessionAction(
            setUserMessage,
            activeSession,
          )(FILE_UPLOAD_STRING.fileNameDisplay(fileNames)),
        );
        dispatch(
          createSessionAction(
            setUserInput,
            activeSession,
          )({
            type: 'SINGLE_INPUT',
            value: FILE_UPLOAD_STRING.fileNameDisplay(fileNames),
          }),
        );
        sendQAMessage(
          FILE_UPLOAD_STRING.fileNameDisplay(fileNames),
          FILE_UPLOAD_STRING.fileNameDisplay(fileNames),
        );
      }}
      onError={(message) => {
        alert(message ?? FILE_UPLOAD_STRING.uploadError);
      }}
    />
  );
};
